import React, { useContext, useEffect } from 'react';
import BasicColorPicker from '../../../../components/BasicColorPicker';
import ClassicTour from '../../../../assets/images/classic.tour.png';
import VirtuaTour from '../../../../assets/images/virtua.tour.png';
import ShowiTour from '../../../../assets/images/showi.tour.png';
import { ListingDetails_ListingData_listing } from '../../../../types/graph/ListingDetails_ListingData';
import * as _ from 'lodash';
import { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { AuthContext } from '../../../../context/AuthContext';
import getModalContext from '../../../../context/getModalContext';
import { gql, useQuery } from '@apollo/client';
import { ListingWebsite_AccountData } from '../../../../types/graph/ListingWebsite_AccountData';
import { ListingWebsite_AccountStatsData } from '../../../../types/graph/ListingWebsite_AccountStatsData';

const disabledTemplateList = ['Virtua', 'Showi'];
//const disabledTemplateList = ['Virtua'];

const settings = [
  {
    name: 'Classic',
    description: 'The template that started it all',
    image: ClassicTour,
  },
  {
    name: 'Virtua',
    description: 'A modern clean layout',
    image: VirtuaTour,
  },
  {
    name: 'Showi',
    description: 'A slide-in window style without scrolling',
    image: ShowiTour,
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface IProps {
  listing: ListingDetails_ListingData_listing;
  onChange: (values: IStylingValues) => void;
}

export interface IStylingValues {
  template?: string;
  colors?: {
    primary?: string;
    accent?: string;
  };
}

const Styling: React.FC<IProps> = ({ listing, onChange }) => {
  const { getAccountId } = useContext(AuthContext);
  const modals = useContext(getModalContext());
  const [canChangeColor, setCanChangeColor] = useState<boolean>(false);

  const { data: accountData, refetch: accountDataRefetch } =
    useQuery<ListingWebsite_AccountData>(GET_ACCOUNT, {
      variables: { accountId: getAccountId() },
    });

  useEffect(() => {
    accountDataRefetch();
  }, [accountDataRefetch]);

  useEffect(() => {
    if (accountData?.account?.package?.group) {
      if (
        accountData?.account?.package?.group === 'package_1' ||
        accountData?.account?.package?.group === 'package_2'
      ) {
        setCanChangeColor(true);
      }
    }
  }, [accountData]);

  const onColorChange = (color: string) => {
    if (canChangeColor) {
      onChange({ colors: { primary: color } });
    } else {
      modals?.setModalState('upgradePackageFeaturesModal', true, {
        account: accountData?.account,
      });
    }
  };

  const onTemplateChange = (template: string) => {
    onChange({ template });
  };

  if (!accountData) {
    return null;
  }

  return (
    <div className="md:grid md:grid-cols-3 md:gap-6">
      <div className="md:col-span-1">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Website Design
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Select some design options for the listing tour website.
        </p>
      </div>
      <div className="mt-5 md:mt-0 md:col-span-2">
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="color"
              className="block text-sm font-medium text-gray-700"
            >
              Choose primary color
            </label>
            <div className="mt-2">
              <BasicColorPicker
                colors={[
                  '#0191C8',
                  '#D32F2F',
                  '#8BC34A',
                  '#FFC107',
                  '#9E9E9E',
                  '#D9622A',
                  '#002349',
                ]}
                selected={
                  listing.website?.styling?.colors?.primary || '#0191C8'
                }
                onSelect={(color: any) => {
                  onColorChange(color);
                }}
              />
            </div>
          </div>
          <div className="col-span-6 sm:col-span-4">
            <RadioGroup
              value={_.get(listing, 'website.styling.template', 'Classic')}
              onChange={onTemplateChange}
            >
              <RadioGroup.Label className="sr-only">
                Choose the template
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {settings.map((setting, settingIdx) => (
                  <RadioGroup.Option
                    key={setting.name}
                    value={setting.name}
                    disabled={
                      _.includes(disabledTemplateList, setting.name)
                        ? true
                        : false
                    }
                    className={({ checked }) =>
                      classNames(
                        settingIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                        settingIdx === settings.length - 1
                          ? 'rounded-bl-md rounded-br-md'
                          : '',
                        checked
                          ? 'bg-indigo-50 border-indigo-200 z-10'
                          : 'border-gray-200',
                        'relative border p-4 flex cursor-pointer focus:outline-none'
                      )
                    }
                  >
                    {({ active, checked }) => (
                      <>
                        <span
                          className={classNames(
                            checked
                              ? 'bg-primary border-transparent'
                              : 'bg-white border-gray-300',
                            active
                              ? 'ring-2 ring-offset-2 ring-primary-light'
                              : '',
                            'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center'
                          )}
                          aria-hidden="true"
                        >
                          <span className="rounded-full bg-white w-1.5 h-1.5" />
                        </span>
                        <div className="ml-3 flex w-full">
                          <div className="flex flex-col flex-grow">
                            <RadioGroup.Label
                              as="span"
                              className={classNames(
                                checked ? 'text-primary-dark' : 'text-gray-900',
                                'block text-sm font-medium'
                              )}
                            >
                              {setting.name}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as="span"
                              className={classNames(
                                checked ? 'text-primary-dark' : 'text-gray-500',
                                'block text-sm'
                              )}
                            >
                              {setting.description}
                              {_.includes(
                                disabledTemplateList,
                                setting.name
                              ) ? (
                                <p className="mt-2 text-gray-700">
                                  Coming Soon! New Look & Feel
                                </p>
                              ) : (
                                ''
                              )}
                            </RadioGroup.Description>
                          </div>
                          <div>
                            <img
                              className={`
                                ${
                                  _.includes(disabledTemplateList, setting.name)
                                    ? `opacity-50`
                                    : `opacity-100`
                                } w-32 rounded-md`}
                              src={setting.image}
                              alt="Template"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Styling;

export const GET_ACCOUNT = gql`
  query ListingWebsite_AccountData($accountId: String!) {
    account(accountId: $accountId) {
      id
      package {
        packageId
        group
        name
        numListings
        numUsers
        flags
      }
    }
  }
`;
